/**
 * EffluentStudyPage.tsx — Effluent Quality & Compliance Study
 *
 * Deep compliance analysis of effluent water samples stored for the active
 * customer session. Assesses:
 *  - Parameter-level pass/fail against the active discharge standard
 *  - Removal efficiency vs paired influent samples (if available)
 *  - Trend analysis over multiple samples
 *  - Regulatory risk matrix with consequence mapping
 *  - Corrective action recommendations per exceedance
 *  - Printable/saveable compliance report
 */

import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  Beaker, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown,
  Minus, BarChart3, ShieldCheck, Shield, FileText, Save, Printer,
  RefreshCw, ArrowUp, ArrowDown, Info, ClipboardList, Building2,
  XCircle, ArrowRight, ChevronDown, ChevronUp, Activity, Layers,
  Zap, Circle, FlaskConical, Gauge,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { toast } from 'sonner';
import { getAllLocalSamples } from '@/lib/hybridStorage';
import { apiListInfluentSamples, apiSaveReport, hasPhpBackend } from '@/lib/phpApi';
import {
  assessCompliance, calcRemovalEfficiencies, assessRegulatoryRisk,
  PARAM_META,
  type ComplianceParameter, type RemovalEfficiency, type RegulatoryRisk,
} from '@/lib/treatabilityAnalysis';
import { ALL_STANDARDS, type StandardCode } from '@/types/standards';
import type { WaterParameters } from '@/types/calculator';

// ─── Standard switcher options ────────────────────────────────────────────
const STANDARD_OPTIONS: { code: StandardCode; label: string; flag: string; color: string }[] = [
  { code: 'UK_EA',    label: 'UK EA',    flag: '🇬🇧', color: '#3b82f6' },
  { code: 'AU_EPA',   label: 'AU EPA',   flag: '🇦🇺', color: '#10b981' },
  { code: 'SA_WATER', label: 'SA Water', flag: '🪣',  color: '#06b6d4' },
  { code: 'EU_UWWTD', label: 'EU UWWTD', flag: '🇪🇺', color: '#a78bfa' },
  { code: 'US_EPA',   label: 'US EPA',   flag: '🇺🇸', color: '#f97316' },
];

// ─── Types ─────────────────────────────────────────────────────────────────

interface SampleRow {
  id: number;
  sampleName: string;
  reportNumber?: string | null;
  params: Record<string, number>;
  createdAt: string;
  sampleType?: string | null;
  source: 'local' | 'server';
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function paramsToRecord(p: Record<string, number>): Record<string, number> {
  return {
    bod5: p.bod5 ?? p.bod ?? 0,
    cod: p.cod ?? 0,
    tss: p.tss ?? 0,
    turbidity: p.turbidity ?? 0,
    temperature: p.temperature ?? 20,
    oilGrease: p.oilGrease ?? p.og ?? p.oandg ?? 0,
    ph: p.ph ?? 7,
    tds: p.tds ?? 0,
    alkalinity: p.alkalinity ?? 0,
    ammoniaN: p.ammoniaN ?? p.nh3n ?? p.nh3 ?? 0,
    totalNitrogen: p.totalNitrogen ?? p.tn ?? 0,
    totalPhosphorus: p.totalPhosphorus ?? p.tp ?? 0,
    conductivity: p.conductivity ?? 0,
    lead: p.lead ?? 0,
    mercury: p.mercury ?? 0,
    cadmium: p.cadmium ?? 0,
    chromium: p.chromium ?? 0,
    nickel: p.nickel ?? 0,
    zinc: p.zinc ?? 0,
    copper: p.copper ?? 0,
    phenol: p.phenol ?? 0,
    sulfides: p.sulfides ?? p.sulfide ?? 0,
    surfactants: p.surfactants ?? 0,
    color: p.color ?? 0,
  };
}

function getStandardLimits(standardCode: string): Record<string, number> {
  const std = ALL_STANDARDS.find(s => s.code === standardCode);
  if (!std) return {};
  const L = std.limits;
  return {
    bod5: L.bod5, cod: L.cod, tss: L.tss,
    ph: L.phMax, ammoniaN: L.ammoniaN ?? 10,
    totalNitrogen: L.totalNitrogen, totalPhosphorus: L.totalPhosphorus,
    oilGrease: L.oilGrease, tds: L.tds,
    conductivity: L.conductivity ?? 2500, temperature: L.temperature ?? 35,
    lead: L.lead, chromium: L.chromium, cadmium: L.cadmium,
    nickel: L.nickel, zinc: L.zinc, copper: L.copper,
    phenol: L.phenol ?? 0.5, sulfides: L.sulfides ?? 1.0, surfactants: L.surfactants ?? 2.0,
  };
}

const CORRECTIVE_ACTIONS: Record<string, string[]> = {
  bod5: ['Increase aeration intensity or SRT', 'Check MLSS concentration and F/M ratio', 'Review influent loading — possible organic shock', 'Consider PAC addition or GAC polishing'],
  cod:  ['Review refractory COD fraction (BOD:COD ratio)', 'Optimise coagulation/flocculation', 'Add GAC or advanced oxidation (Fenton/ozone)', 'Increase biological SRT or add MBBR carriers'],
  tss:  ['Inspect secondary clarifier for sludge blanket rise', 'Check RAS rate and MLSS control', 'Optimise polymer dosing for final filtration', 'Service/clean multimedia or cloth filter'],
  ph:   ['Review acid/alkali dosing rate and setpoint', 'Check dosing pump calibration', 'Inspect pH probe for fouling or drift', 'Review buffering capacity of treated effluent'],
  ammoniaN: ['Confirm nitrification is occurring (DO ≥2 mg/L)', 'Check SRT — nitrifiers need SRT >8 d at 15°C', 'Supplement alkalinity if buffer depleted', 'Review aeration rate in nitrification zone'],
  totalNitrogen: ['Implement anoxic zone for denitrification', 'Optimise internal recycle ratio (3:1 to 5:1)', 'Check carbon source availability for denitrification', 'Consider methanol or glycerol addition'],
  totalPhosphorus: ['Increase FeCl₃ or alum dosing dose', 'Check chemical dosing point location', 'Implement Enhanced Biological P Removal (EBPR)', 'Consider polishing with coagulation/filtration'],
  oilGrease: ['Increase DAF recycle rate or pressure', 'Review coagulant/polymer dosing to DAF', 'Check flotation blanket depth and skimmer operation', 'Inspect grease trap before DAF'],
  tds:  ['Reduce TDS at source (process water reuse)', 'Consider reverse osmosis or nanofiltration', 'Review saline water ingress to system', 'Assess industrial discharge controls'],
  lead: ['Increase pH to >8.5 for metal precipitation', 'Dose sulphide for lead sulphide precipitation', 'Add coagulant and polymer for floc removal', 'Check upstream source and segregate industrial discharge'],
  chromium: ['Reduce Cr(VI) to Cr(III) with sulphite/ferrous', 'Precipitate at pH 8–9 with lime', 'Consider ion exchange for low concentrations', 'Source control and trade effluent consent review'],
};

function getCorrectiveActions(param: string): string[] {
  return CORRECTIVE_ACTIONS[param] ?? ['Review treatment performance for this parameter', 'Increase chemical dosing if applicable', 'Inspect relevant equipment stage', 'Contact process engineer for specialist review'];
}

function getTrendIcon(vals: number[]) {
  if (vals.length < 2) return <Minus className="w-3.5 h-3.5 text-slate-500" />;
  const change = ((vals[vals.length - 1] - vals[0]) / (vals[0] || 1)) * 100;
  if (change > 10)  return <TrendingUp className="w-3.5 h-3.5 text-red-400" />;
  if (change < -10) return <TrendingDown className="w-3.5 h-3.5 text-emerald-400" />;
  return <Minus className="w-3.5 h-3.5 text-slate-500" />;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, children, accent = '#10b981' }: {
  title: string; icon: React.ElementType; children: React.ReactNode; accent?: string;
}) {
  return (
    <div className="rb-card overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
        <h3 className="text-sm font-bold text-slate-200 tracking-tight">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function ComplianceMeter({ score }: { score: number }) {
  const color = score >= 90 ? '#10b981' : score >= 70 ? '#84cc16' : score >= 50 ? '#f59e0b' : score >= 30 ? '#f97316' : '#ef4444';
  const r = 52; const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <svg width={128} height={128} viewBox="0 0 128 128">
      <circle cx={64} cy={64} r={r} fill="none" stroke="rgba(100,116,139,0.15)" strokeWidth={10} />
      <circle cx={64} cy={64} r={r} fill="none" stroke={color} strokeWidth={10}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transformOrigin: '64px 64px', transform: 'rotate(-90deg)', transition: 'stroke-dasharray 0.8s ease' }} />
      <text x={64} y={60} textAnchor="middle" fill="white" fontSize={22} fontWeight={700}>{score}%</text>
      <text x={64} y={75} textAnchor="middle" fill="#94a3b8" fontSize={10}>Compliance</text>
    </svg>
  );
}

function RiskBadge({ level }: { level: RegulatoryRisk['level'] }) {
  const map = {
    low:      { label: 'Low Risk',     cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-800/50' },
    moderate: { label: 'Moderate',     cls: 'bg-amber-500/10 text-amber-400 border-amber-800/50' },
    high:     { label: 'High Risk',    cls: 'bg-red-500/10 text-red-400 border-red-800/50' },
    critical: { label: 'CRITICAL',     cls: 'bg-red-600 text-white border-red-600' },
  };
  const { label, cls } = map[level];
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${cls}`}>{label}</span>;
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function EffluentStudyPage() {
  const { currentCustomer, activeStandard } = useSession();
  const [effluentSamples, setEffluentSamples] = useState<SampleRow[]>([]);
  const [influentSamples, setInfluentSamples] = useState<SampleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedEffId, setSelectedEffId] = useState<number | null>(null);
  const [pairedInfluId, setPairedInfluId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'compliance' | 'parameters' | 'removal' | 'trend' | 'risk' | 'report'>('compliance');
  const [saving, setSaving] = useState(false);
  const [expandedParam, setExpandedParam] = useState<string | null>(null);
  const [complianceStandard, setComplianceStandard] = useState<StandardCode | null>(null);

  // Active standard: if user overrode it, use that; else use session standard
  const activeComplianceCode = complianceStandard ?? (activeStandard as StandardCode) ?? 'UK_EA';
  const limits = useMemo(() => getStandardLimits(activeComplianceCode), [activeComplianceCode]);

  // Load samples
  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      const local = getAllLocalSamples();
      const eff = local.filter(s => s.sampleType === 'effluent').map((s): SampleRow => ({
        id: s.id, sampleName: s.sampleName, reportNumber: s.reportNumber,
        params: s.params, createdAt: s.createdAt, sampleType: s.sampleType, source: 'local',
      }));
      const inf = local.filter(s => s.sampleType === 'influent' || !s.sampleType).map((s): SampleRow => ({
        id: s.id, sampleName: s.sampleName, reportNumber: s.reportNumber,
        params: s.params, createdAt: s.createdAt, sampleType: s.sampleType, source: 'local',
      }));

      let serverEff: SampleRow[] = [];
      let serverInf: SampleRow[] = [];
      if (hasPhpBackend()) {
        try {
          const [effRes, infRes] = await Promise.all([
            apiListInfluentSamples(currentCustomer?.serverId, { type: 'effluent' }),
            apiListInfluentSamples(currentCustomer?.serverId, { type: 'influent' }),
          ]);
          const toRow = (s: Record<string, unknown>, type: string): SampleRow => ({
            id: s.id as number,
            sampleName: (s.sampleName ?? s.sample_name ?? 'Sample') as string,
            reportNumber: (s.reportNumber ?? s.report_number ?? null) as string | null,
            params: (s.params ?? {}) as Record<string, number>,
            createdAt: (s.createdAt ?? s.created_at ?? '') as string,
            sampleType: type, source: 'server',
          });
          serverEff = (effRes ?? []).map((s: Record<string, unknown>) => toRow(s, 'effluent'));
          serverInf = (infRes ?? []).map((s: Record<string, unknown>) => toRow(s, 'influent'));
        } catch { /* server unavailable */ }
      }

      const dedup = (arr: SampleRow[]) => {
        const seen = new Set<number>();
        return arr.filter(s => !seen.has(s.id) && seen.add(s.id));
      };

      const merged_eff = dedup([...serverEff, ...eff]).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const merged_inf = dedup([...serverInf, ...inf]).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setEffluentSamples(merged_eff);
      setInfluentSamples(merged_inf);
      if (merged_eff.length > 0 && !selectedEffId) setSelectedEffId(merged_eff[0].id);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [currentCustomer, selectedEffId]);

  useMemo(() => { if (!loaded) loadSamples(); }, [loaded, loadSamples]);

  const selectedEff = useMemo(() => effluentSamples.find(s => s.id === selectedEffId), [effluentSamples, selectedEffId]);
  const pairedInflu = useMemo(() => influentSamples.find(s => s.id === pairedInfluId), [influentSamples, pairedInfluId]);

  // Run compliance analysis
  const effParams = useMemo(() => selectedEff ? paramsToRecord(selectedEff.params) : {}, [selectedEff]);
  const infParams = useMemo(() => pairedInflu ? paramsToRecord(pairedInflu.params) : {}, [pairedInflu]);

  const compliance = useMemo((): ComplianceParameter[] => {
    if (!selectedEff) return [];
    return assessCompliance(effParams, limits);
  }, [effParams, limits, selectedEff]);

  const removals = useMemo((): RemovalEfficiency[] => {
    if (!pairedInflu) return [];
    return calcRemovalEfficiencies(infParams, effParams);
  }, [infParams, effParams, pairedInflu]);

  const riskAssessment = useMemo((): RegulatoryRisk | null => {
    if (compliance.length === 0) return null;
    return assessRegulatoryRisk(compliance);
  }, [compliance]);

  // Compliance score
  const complianceScore = useMemo(() => {
    const checked = compliance.filter(c => c.limit > 0);
    if (checked.length === 0) return 100;
    return Math.round((checked.filter(c => c.pass).length / checked.length) * 100);
  }, [compliance]);

  // Trend data for multiple effluent samples (last 6)
  const trendData = useMemo(() => {
    const recent = effluentSamples.slice(0, 6).reverse();
    return recent.map(s => {
      const p = paramsToRecord(s.params);
      return {
        name: new Date(s.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        BOD: p.bod5, COD: p.cod, TSS: p.tss, pH: p.ph, NH3: p.ammoniaN,
      };
    });
  }, [effluentSamples]);

  // Bar chart data
  const complianceChart = useMemo(() =>
    compliance.slice(0, 12).map(c => ({
      name: c.label.length > 8 ? c.label.substring(0, 8) : c.label,
      ratio: Math.round(c.ratio * 100),
      color: c.color,
    })), [compliance]);

  // Radar for removal efficiency
  const removalRadar = useMemo(() =>
    removals.slice(0, 7).map(r => ({ subject: r.label, efficiency: Math.round(r.removalPct), expected: r.expectedMin })),
    [removals]
  );

  const handleSaveReport = async () => {
    if (!selectedEff) return;
    setSaving(true);
    try {
      const payload = {
        reportType: 'effluent_study',
        customerId: currentCustomer?.serverId ?? null,
        sampleId: selectedEff.id,
        sampleName: selectedEff.sampleName,
        standard: activeStandard,
        complianceScore,
        compliance,
        removals,
        riskLevel: riskAssessment?.level,
        generatedAt: new Date().toISOString(),
        title: `Effluent Compliance Study — ${selectedEff.sampleName}`,
      };
      if (hasPhpBackend()) {
        await apiSaveReport(payload);
        toast.success('Compliance report saved to database');
      } else {
        localStorage.setItem(`rb_effluent_study_${selectedEff.id}`, JSON.stringify({ ...payload, savedAt: new Date().toISOString() }));
        toast.success('Compliance report saved locally');
      }
    } catch {
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'compliance',  label: 'Compliance Dashboard', short: 'Compliance', icon: ShieldCheck,    color: '#10b981' },
    { id: 'parameters',  label: 'Parameter Analysis',   short: 'Parameters', icon: FlaskConical,   color: '#06b6d4' },
    { id: 'removal',     label: 'Removal Efficiency',   short: 'Removal',    icon: Activity,       color: '#a855f7' },
    { id: 'trend',       label: 'Trend Analysis',        short: 'Trends',     icon: TrendingUp,     color: '#3b82f6' },
    { id: 'risk',        label: 'Risk & Actions',        short: 'Risk',       icon: AlertTriangle,  color: '#f43f5e' },
    { id: 'report',      label: 'Study Report',          short: 'Report',     icon: FileText,       color: '#f59e0b' },
  ] as const;

  const standardName = ALL_STANDARDS.find(s => s.code === activeComplianceCode)?.name ?? activeComplianceCode;
  const activeStandardOption = STANDARD_OPTIONS.find(o => o.code === activeComplianceCode);

  return (
    <div className="space-y-6 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        icon={Beaker}
        title="Effluent Quality & Compliance Study"
        subtitle={currentCustomer ? `${currentCustomer.name} — Standard: ${standardName}` : `Discharge standard: ${standardName}`}
        iconColor="text-emerald-400"
        badge={riskAssessment && <RiskBadge level={riskAssessment.level} />}
        actions={
          <div className="flex gap-2">
            <button onClick={loadSamples} disabled={loading}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-slate-200 bg-rb-elevated border border-slate-800/60 rounded-lg transition-colors flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {selectedEff && (
              <button onClick={handleSaveReport} disabled={saving}
                className="px-3 py-1.5 text-xs font-bold text-black bg-rb-brand hover:bg-yellow-400 rounded-lg transition-colors flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving…' : 'Save Report'}
              </button>
            )}
          </div>
        }
      />

      {/* Sample Selection Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Effluent sample */}
        <div className="rb-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
              <Beaker className="w-4 h-4 text-emerald-400" />
              Select Effluent Sample
            </h3>
            <span className="text-[11px] text-slate-500">{effluentSamples.length} available</span>
          </div>
          {loading && <div className="text-xs text-slate-500 text-center py-4 flex items-center justify-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" />Loading…</div>}
          {!loading && effluentSamples.length === 0 && (
            <div className="text-center py-5 text-slate-500 text-xs">
              <Beaker className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
              No effluent samples found. Add via <strong className="text-slate-400">Effluent Entry</strong>.
            </div>
          )}
          <div className="space-y-1.5 max-h-44 overflow-y-auto">
            {effluentSamples.map(s => {
              const sel = s.id === selectedEffId;
              const date = new Date(s.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
              return (
                <button key={s.id} onClick={() => { setSelectedEffId(s.id); setActiveTab('compliance'); }}
                  className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-all flex items-center justify-between ${sel
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'}`}>
                  <div>
                    <div className="text-xs font-bold text-slate-200">{s.sampleName}</div>
                    <div className="text-[10px] text-slate-500">{date} · {s.reportNumber ?? 'No ref'}</div>
                  </div>
                  {sel && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Pair with influent */}
        <div className="rb-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
              <Layers className="w-4 h-4 text-blue-400" />
              Pair with Influent Sample <span className="text-[10px] text-slate-600 font-normal">(optional — enables removal efficiency)</span>
            </h3>
          </div>
          <div className="space-y-1.5 max-h-44 overflow-y-auto">
            <button onClick={() => setPairedInfluId(null)}
              className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-all ${!pairedInfluId
                ? 'border-slate-600 bg-slate-800/50'
                : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'}`}>
              <span className="text-xs text-slate-500">— No pairing (compliance only)</span>
            </button>
            {influentSamples.map(s => {
              const sel = s.id === pairedInfluId;
              const date = new Date(s.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
              return (
                <button key={s.id} onClick={() => setPairedInfluId(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-all flex items-center justify-between ${sel
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'}`}>
                  <div>
                    <div className="text-xs font-bold text-slate-200">{s.sampleName}</div>
                    <div className="text-[10px] text-slate-500">{date}</div>
                  </div>
                  {sel && <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Analysis Panel */}
      <AnimatePresence mode="wait">
        {selectedEff && (
          <motion.div key={selectedEffId} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {/* KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Compliance Score', value: `${complianceScore}%`, color: complianceScore >= 90 ? '#10b981' : complianceScore >= 70 ? '#f59e0b' : '#ef4444', desc: `${compliance.filter(c => c.pass).length}/${compliance.length} params pass` },
                { label: 'Exceedances', value: String(compliance.filter(c => !c.pass).length), color: compliance.filter(c => !c.pass).length === 0 ? '#10b981' : '#ef4444', desc: compliance.filter(c => !c.pass).map(c => c.label).join(', ') || 'None' },
                { label: 'Near-Limit Params', value: String(compliance.filter(c => c.riskLevel === 'near_limit').length), color: '#f59e0b', desc: 'Within 20% of limit' },
                { label: 'Regulatory Risk', value: riskAssessment?.level?.toUpperCase() ?? '—', color: riskAssessment?.level === 'low' ? '#10b981' : riskAssessment?.level === 'moderate' ? '#f59e0b' : '#ef4444', desc: `Score: ${riskAssessment?.score ?? 0}/100` },
              ].map((kpi, i) => (
                <div key={i} className="rb-card p-3 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{kpi.label}</div>
                  <div className="text-xl font-bold mt-1 capitalize" style={{ color: kpi.color }}>{kpi.value}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={kpi.desc}>{kpi.desc}</div>
                </div>
              ))}
            </div>

            {/* Tabs — coloured pills with shadow + slow pulse on active (matches InfluentStudy / CustomerSite) */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    title={tab.label}
                    className="relative flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl text-[11px] sm:text-xs font-bold uppercase tracking-wide transition-all border-2 overflow-hidden"
                    style={{
                      background: active
                        ? `linear-gradient(135deg, ${tab.color}38, ${tab.color}12)`
                        : `linear-gradient(135deg, ${tab.color}14, ${tab.color}06)`,
                      borderColor: active ? tab.color : `${tab.color}55`,
                      color: active ? '#f8fafc' : tab.color,
                      boxShadow: active
                        ? `0 4px 14px ${tab.color}55, 0 0 0 1px ${tab.color}88 inset, 0 0 22px ${tab.color}44`
                        : `0 2px 6px ${tab.color}25, 0 0 0 1px ${tab.color}33 inset`,
                    }}
                  >
                    {active && (
                      <span
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{ animation: 'rb-tab-pulse 2.6s ease-in-out infinite' }}
                      />
                    )}
                    <Icon className="w-4 h-4 shrink-0" style={{ filter: active ? `drop-shadow(0 0 4px ${tab.color}cc)` : 'none' }} />
                    <span className="leading-tight text-center sm:text-left">{tab.short}</span>
                  </button>
                );
              })}
            </div>
            <style>{`
              @keyframes rb-tab-pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.0); opacity: 0.85; }
                50%      { box-shadow: 0 0 0 6px rgba(255,255,255,0.0); opacity: 1; }
              }
            `}</style>

            {/* Tab: Compliance Dashboard */}
            {activeTab === 'compliance' && (
              <div className="space-y-4">
              {/* ── Discharge Standard Switcher ─────────────────────────────── */}
              <div className="rb-card p-3.5">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 shrink-0">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-bold text-slate-400">Discharge Standard:</span>
                    {!complianceStandard && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-rb-brand/15 text-rb-brand border border-rb-brand/30 font-bold">Session Default</span>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {STANDARD_OPTIONS.map(opt => {
                      const isActive = activeComplianceCode === opt.code;
                      const isSessionDefault = !complianceStandard && opt.code === (activeStandard as StandardCode);
                      return (
                        <button key={opt.code}
                          onClick={() => setComplianceStandard(opt.code === (activeStandard as StandardCode) && !complianceStandard ? null : opt.code)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${isActive
                            ? 'text-slate-900'
                            : 'border-slate-800/60 text-slate-500 hover:text-slate-300 hover:border-slate-700 bg-rb-elevated'}`}
                          style={isActive ? { background: opt.color, borderColor: opt.color } : {}}>
                          <span>{opt.flag}</span>
                          {opt.label}
                          {isSessionDefault && <span className="text-[9px] opacity-70">(active)</span>}
                        </button>
                      );
                    })}
                    {complianceStandard && complianceStandard !== (activeStandard as StandardCode) && (
                      <button onClick={() => setComplianceStandard(null)}
                        className="px-2.5 py-1.5 rounded-xl text-[10px] font-bold text-slate-500 hover:text-slate-300 bg-rb-elevated border border-slate-800/60 transition-colors">
                        ↺ Reset to session
                      </button>
                    )}
                  </div>
                  <div className="ml-auto flex items-center gap-2 shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: activeStandardOption?.color ?? '#10b981' }} />
                    <span className="text-[11px] text-slate-400 font-medium">{standardName}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Gauge + summary */}
                <SectionCard title="Overall Compliance" icon={Gauge} accent="#10b981">
                  <div className="flex flex-col items-center gap-3">
                    <ComplianceMeter score={complianceScore} />
                    <div className="text-center">
                      <div className="text-sm font-bold text-slate-200">{standardName}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{compliance.filter(c => c.pass).length} of {compliance.length} parameters compliant</div>
                    </div>
                    {riskAssessment && (
                      <div className="w-full pt-3 border-t border-slate-800/50 flex justify-center">
                        <RiskBadge level={riskAssessment.level} />
                      </div>
                    )}
                  </div>
                </SectionCard>

                {/* Compliance chart */}
                <div className="lg:col-span-2">
                  <SectionCard title="Parameter Compliance Ratio (value / limit)" icon={BarChart3} accent="#3b82f6">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={complianceChart} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }}
                          formatter={(v: number) => [`${v}%`, 'Value as % of limit']} />
                        <ReferenceLine y={100} stroke="#ef4444" strokeDasharray="4 2"
                          label={{ value: 'Limit = 100%', fill: '#ef4444', fontSize: 9, position: 'insideTopRight' }} />
                        <Bar dataKey="ratio" radius={[3, 3, 0, 0]}>
                          {complianceChart.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionCard>
                </div>

                {/* Failed params list */}
                {compliance.filter(c => !c.pass).length > 0 && (
                  <div className="lg:col-span-3">
                    <SectionCard title="Exceedances — Immediate Action Required" icon={AlertTriangle} accent="#ef4444">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {compliance.filter(c => !c.pass).map(c => (
                          <div key={c.param} className="p-3 rounded-xl bg-red-500/5 border border-red-800/30">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-bold text-red-400">{c.label}</span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.riskLevel === 'critical' ? 'bg-red-600 text-white' : 'bg-red-500/20 text-red-400'}`}>
                                {c.riskLevel === 'critical' ? 'CRITICAL' : 'EXCEEDED'}
                              </span>
                            </div>
                            <div className="text-xs text-slate-400">
                              <span className="font-bold text-slate-200">{c.value.toFixed(c.value < 10 ? 2 : 0)}</span>
                              <span className="text-slate-600"> {c.unit} vs limit </span>
                              <span className="font-bold">{c.limit.toFixed(c.limit < 10 ? 2 : 0)} {c.unit}</span>
                              <span className="ml-2 font-mono text-red-400">+{Math.round(Math.abs(c.margin))}% over</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </SectionCard>
                  </div>
                )}
              </div>
              </div>
            )}

            {/* Tab: Parameter Analysis */}
            {activeTab === 'parameters' && (
              <div className="space-y-2">
                <SectionCard title="Full Parameter Compliance Table" icon={ClipboardList} accent="#3b82f6">
                  {compliance.length === 0 ? (
                    <div className="text-center py-6 text-xs text-slate-500">No parameter data to display — check sample data and selected standard.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-rb-elevated">
                            <th className="text-left px-3 py-2 font-bold">Parameter</th>
                            <th className="text-right px-3 py-2 font-bold">Measured</th>
                            <th className="text-right px-3 py-2 font-bold">Limit ({standardName.split(' ')[0]})</th>
                            <th className="text-right px-3 py-2 font-bold">% of Limit</th>
                            <th className="text-right px-3 py-2 font-bold">Margin</th>
                            <th className="text-center px-3 py-2 font-bold">Status</th>
                            <th className="text-center px-3 py-2 font-bold w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {compliance.map(c => (
                            <>
                              <tr key={c.param}
                                className={`border-t border-slate-800/40 cursor-pointer hover:bg-rb-elevated/50 transition-colors ${!c.pass ? 'bg-red-500/3' : ''}`}
                                onClick={() => setExpandedParam(expandedParam === c.param ? null : c.param)}>
                                <td className="px-3 py-2.5 font-semibold text-slate-300">{c.label}</td>
                                <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: c.color }}>
                                  {c.value.toFixed(c.value < 10 ? 2 : 0)} <span className="text-slate-600 text-[10px]">{c.unit}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono text-slate-400">
                                  {c.limit.toFixed(c.limit < 10 ? 2 : 0)} <span className="text-slate-600 text-[10px]">{c.unit}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, c.ratio * 100)}%`, background: c.color }} />
                                    </div>
                                    <span className="font-mono font-bold text-[11px]" style={{ color: c.color }}>{Math.round(c.ratio * 100)}%</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono text-[11px]">
                                  <span className={c.pass ? 'text-emerald-400' : 'text-red-400'}>
                                    {c.pass ? `${Math.round(c.margin)}% under` : `${Math.round(Math.abs(c.margin))}% over`}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  {c.pass
                                    ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" />
                                    : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}
                                </td>
                                <td className="px-3 py-2.5 text-center text-slate-600">
                                  {expandedParam === c.param ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </td>
                              </tr>
                              {expandedParam === c.param && (
                                <tr key={`${c.param}-actions`}>
                                  <td colSpan={8} className="px-4 pb-3 pt-1 bg-rb-elevated/30">
                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Corrective Actions</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                      {getCorrectiveActions(c.param).map((action, i) => (
                                        <div key={i} className="flex items-start gap-1.5 text-xs text-slate-400">
                                          <ArrowRight className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                                          {action}
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </div>
            )}

            {/* Tab: Removal Efficiency */}
            {activeTab === 'removal' && (
              <div className="space-y-4">
                {!pairedInflu ? (
                  <div className="rb-card p-8 text-center">
                    <Layers className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                    <div className="text-sm font-bold text-slate-400 mb-1">No Influent Sample Paired</div>
                    <p className="text-xs text-slate-500">Select an influent sample above to calculate removal efficiencies and process performance.</p>
                  </div>
                ) : (
                  <>
                    <SectionCard title="Removal Efficiency vs Expected Performance" icon={Activity} accent="#10b981">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Radar */}
                        {removalRadar.length >= 3 ? (
                          <ResponsiveContainer width="100%" height={260}>
                            <RadarChart data={removalRadar}>
                              <PolarGrid stroke="rgba(100,116,139,0.15)" />
                              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                              <Radar name="Achieved" dataKey="efficiency" stroke="#10b981" fill="#10b981" fillOpacity={0.2} strokeWidth={2} />
                              <Radar name="Expected Min" dataKey="expected" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.05} strokeWidth={1.5} strokeDasharray="4 2" />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Tooltip formatter={(v: number) => [`${Math.round(v)}%`, '']} />
                            </RadarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-slate-600">
                            <Activity className="w-8 h-8 opacity-20" />
                            <p className="text-xs">Pair an influent sample to enable removal radar</p>
                          </div>
                        )}

                        {/* Table */}
                        <div className="space-y-2">
                          {removals.map(r => {
                            const perfColor: Record<string, string> = { excellent: '#10b981', good: '#84cc16', adequate: '#f59e0b', poor: '#f97316', failing: '#ef4444' };
                            const color = perfColor[r.performance] ?? '#94a3b8';
                            return (
                              <div key={r.param} className="flex items-center gap-3 p-2.5 rounded-lg bg-rb-elevated border border-slate-800/40">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-bold text-slate-300">{r.label}</span>
                                    <span className="text-[10px] font-bold capitalize" style={{ color }}>{r.performance}</span>
                                  </div>
                                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, r.removalPct)}%`, background: color }} />
                                  </div>
                                  <div className="flex justify-between text-[10px] text-slate-600 mt-0.5">
                                    <span>{r.influentValue.toFixed(0)} → {r.effluentValue.toFixed(0)} {r.unit}</span>
                                    <span>{Math.round(r.removalPct)}% removed (exp. {r.expectedMin}–{r.expectedMax}%)</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </SectionCard>
                  </>
                )}
              </div>
            )}

            {/* Tab: Trend Analysis */}
            {activeTab === 'trend' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="BOD₅, COD & TSS Trend" icon={TrendingUp} accent="#3b82f6">
                  {trendData.length < 2 ? (
                    <div className="text-center py-8 text-xs text-slate-500">Minimum 2 effluent samples required for trend analysis.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={trendData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {limits.bod5  > 0 && <ReferenceLine y={limits.bod5}  stroke="#10b981" strokeDasharray="4 2" label={{ value: `BOD limit ${limits.bod5}`, fill: '#10b981', fontSize: 9 }} />}
                        {limits.cod   > 0 && <ReferenceLine y={limits.cod}   stroke="#3b82f6" strokeDasharray="4 2" label={{ value: `COD limit ${limits.cod}`, fill: '#3b82f6', fontSize: 9 }} />}
                        {limits.tss   > 0 && <ReferenceLine y={limits.tss}   stroke="#06b6d4" strokeDasharray="4 2" label={{ value: `TSS limit ${limits.tss}`, fill: '#06b6d4', fontSize: 9 }} />}
                        <Line type="monotone" dataKey="BOD" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="COD" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="TSS" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </SectionCard>

                <SectionCard title="pH & NH₃-N Trend" icon={TrendingUp} accent="#a78bfa">
                  {trendData.length < 2 ? (
                    <div className="text-center py-8 text-xs text-slate-500">Minimum 2 samples required.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={trendData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <ReferenceLine yAxisId="left" y={6} stroke="#f59e0b" strokeDasharray="3 2" />
                        <ReferenceLine yAxisId="left" y={9} stroke="#f59e0b" strokeDasharray="3 2" />
                        <Line yAxisId="left" type="monotone" dataKey="pH" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                        <Line yAxisId="right" type="monotone" dataKey="NH3" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </SectionCard>

                {/* Trend summary */}
                {trendData.length >= 2 && (
                  <div className="lg:col-span-2">
                    <SectionCard title="Trend Direction Summary" icon={Activity} accent="#f59e0b">
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        {(['BOD', 'COD', 'TSS', 'pH', 'NH3'] as const).map(param => {
                          const vals = trendData.map(d => d[param] ?? 0).filter(v => v > 0);
                          const change = vals.length >= 2 ? ((vals[vals.length - 1] - vals[0]) / (vals[0] || 1)) * 100 : 0;
                          const improving = param === 'pH' ? Math.abs(change) < 5 : change < -5;
                          const colour = improving ? '#10b981' : Math.abs(change) < 5 ? '#94a3b8' : '#ef4444';
                          return (
                            <div key={param} className="p-3 rounded-xl bg-rb-elevated border border-slate-800/40 text-center">
                              <div className="text-[10px] text-slate-500 font-bold uppercase">{param === 'NH3' ? 'NH₃-N' : param}</div>
                              <div className="my-1.5 flex justify-center">{getTrendIcon(vals)}</div>
                              <div className="text-xs font-bold font-mono" style={{ color: colour }}>
                                {change > 0 ? '+' : ''}{change.toFixed(1)}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </SectionCard>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Risk & Actions */}
            {activeTab === 'risk' && riskAssessment && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Regulatory Risk Assessment" icon={Shield} accent={riskAssessment.level === 'low' ? '#10b981' : riskAssessment.level === 'moderate' ? '#f59e0b' : '#ef4444'}>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-bold capitalize" style={{ color: riskAssessment.level === 'low' ? '#10b981' : riskAssessment.level === 'moderate' ? '#f59e0b' : '#ef4444' }}>
                          {riskAssessment.level} Regulatory Risk
                        </div>
                        <div className="text-xs text-slate-500">Risk score: {riskAssessment.score}/100</div>
                      </div>
                      <RiskBadge level={riskAssessment.level} />
                    </div>

                    <div>
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Regulatory Consequence</div>
                      {riskAssessment.consequenceNotes.map((n, i) => (
                        <div key={i} className="flex items-start gap-2 py-1.5 text-xs text-slate-400">
                          <Circle className="w-2 h-2 mt-1 shrink-0 text-slate-600" style={{ fill: '#475569' }} />
                          {n}
                        </div>
                      ))}
                    </div>

                    {riskAssessment.exceedingParams.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1.5">Exceeding Parameters</div>
                        <div className="flex flex-wrap gap-1.5">
                          {riskAssessment.exceedingParams.map(p => (
                            <span key={p} className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-500/10 text-red-400 border border-red-800/30">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {riskAssessment.nearLimitParams.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-1.5">Near-Limit Parameters (&gt;80%)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {riskAssessment.nearLimitParams.map(p => (
                            <span key={p} className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-800/30">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </SectionCard>

                <SectionCard title="Immediate Actions Required" icon={Zap} accent="#f97316">
                  <div className="space-y-2.5">
                    {riskAssessment.immediateActions.map((action, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-rb-elevated border border-slate-800/40">
                        <div className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
                        <span className="text-xs text-slate-400">{action}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                {/* Per-parameter corrective actions */}
                {compliance.filter(c => !c.pass).length > 0 && (
                  <div className="lg:col-span-2">
                    <SectionCard title="Parameter-Specific Corrective Actions" icon={ClipboardList} accent="#3b82f6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {compliance.filter(c => !c.pass).map(c => (
                          <div key={c.param} className="p-3 rounded-xl bg-rb-elevated border border-slate-800/40">
                            <div className="flex items-center gap-2 mb-2">
                              <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                              <span className="text-sm font-bold text-slate-200">{c.label}</span>
                              <span className="text-xs text-red-400 font-mono ml-auto">{c.value.toFixed(c.value < 10 ? 2 : 0)} / {c.limit.toFixed(c.limit < 10 ? 2 : 0)} {c.unit}</span>
                            </div>
                            {getCorrectiveActions(c.param).map((a, i) => (
                              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-400 py-0.5">
                                <ArrowRight className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                                {a}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </SectionCard>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Report */}
            {activeTab === 'report' && (
              <div className="space-y-4">
                <div className="flex justify-end gap-2">
                  <button onClick={() => window.print()}
                    className="px-4 py-2 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-800/60 rounded-xl transition-colors flex items-center gap-2">
                    <Printer className="w-3.5 h-3.5" /> Print Report
                  </button>
                  <button onClick={handleSaveReport} disabled={saving}
                    className="px-4 py-2 text-xs font-bold text-black bg-rb-brand hover:bg-yellow-400 rounded-xl transition-colors flex items-center gap-2">
                    <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save to Database'}
                  </button>
                </div>

                <div className="rb-card p-6 space-y-5">
                  <div className="border-b border-slate-800/50 pb-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-100">Effluent Quality & Compliance Study</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{selectedEff.sampleName}</p>
                        {currentCustomer && <p className="text-sm text-slate-400 mt-0.5">{currentCustomer.name} / {currentCustomer.company}</p>}
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <div>Generated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                        <div>Standard: {standardName}</div>
                        <div className="mt-1 font-bold" style={{ color: complianceScore >= 90 ? '#10b981' : complianceScore >= 70 ? '#f59e0b' : '#ef4444' }}>
                          {complianceScore}% Compliant
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-slate-200 mb-3">Executive Summary</h3>
                    <div className="p-4 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-400 leading-relaxed space-y-2">
                      <p>This compliance study analyses effluent sample <strong className="text-slate-200">{selectedEff.sampleName}</strong> against the <strong className="text-slate-200">{standardName}</strong> discharge standard.</p>
                      <p>Overall compliance score: <strong style={{ color: complianceScore >= 90 ? '#10b981' : complianceScore >= 70 ? '#f59e0b' : '#ef4444' }}>{complianceScore}%</strong> — {compliance.filter(c => c.pass).length} of {compliance.length} parameters within consent limits.</p>
                      {compliance.filter(c => !c.pass).length > 0 ? (
                        <p><strong className="text-red-400">{compliance.filter(c => !c.pass).length} parameter(s) exceeding limits:</strong> {compliance.filter(c => !c.pass).map(c => c.label).join(', ')}. Regulatory risk is assessed as <strong>{riskAssessment?.level?.toUpperCase()}</strong>.</p>
                      ) : (
                        <p><strong className="text-emerald-400">All monitored parameters are within discharge limits.</strong> Continue monitoring as per consent conditions.</p>
                      )}
                      {pairedInflu && removals.length > 0 && (
                        <p>Treatment system achieved average removal of <strong className="text-slate-200">{Math.round(removals.reduce((s, r) => s + r.removalPct, 0) / removals.length)}%</strong> across key parameters.</p>
                      )}
                    </div>
                  </div>

                  {compliance.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 mb-3">Compliance Summary Table</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-rb-elevated text-slate-500 text-[10px] uppercase tracking-wider">
                              <th className="text-left px-3 py-2 font-bold">Parameter</th>
                              <th className="text-right px-3 py-2 font-bold">Measured</th>
                              <th className="text-right px-3 py-2 font-bold">Consent Limit</th>
                              <th className="text-center px-3 py-2 font-bold">Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compliance.map(c => (
                              <tr key={c.param} className="border-t border-slate-800/40">
                                <td className="px-3 py-2 text-slate-400">{c.label}</td>
                                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: c.color }}>
                                  {c.value.toFixed(c.value < 10 ? 2 : 0)} {c.unit}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-slate-500">
                                  {c.limit.toFixed(c.limit < 10 ? 2 : 0)} {c.unit}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {c.pass
                                    ? <span className="text-[10px] font-bold text-emerald-400">PASS</span>
                                    : <span className="text-[10px] font-bold text-red-400">FAIL</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t border-slate-800/40 flex justify-between text-xs text-slate-600">
                    <span>Reynolds & Bauhm — Water Treatment Engineering</span>
                    <span>Confidential — {currentCustomer?.name ?? 'Client'}</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
